import {
  Dispatch, SetStateAction, useCallback, useEffect, useLayoutEffect, useMemo, useRef,
} from 'react';
import {
  RemovalSnapshot,
  restoreRemovedItems,
  restoreRemovedSelection,
  UnassignedOptimistic,
} from 'src/components/dispatch/unassignedOptimistic';
import { UnassignedItem } from 'src/entities/dispatch';

export type UseUnassignedRemovalOptimisticParams = {
  /** サーバ由来の未割当リスト */
  serverItems: UnassignedItem[];
  /** D&D・楽観削除のローカル上書き。null ならサーバ値をそのまま表示している */
  overrideItems: UnassignedItem[] | null;
  setOverrideItems: Dispatch<SetStateAction<UnassignedItem[] | null>>;
  selectedIds: ReadonlySet<string>;
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>;
  /** `${companyId}:${startOn}:${endOn}` */
  scopeKey: string;
};

/** 並びまで含めて同じ案件列か。参照比較だと setState の度に別配列になるので使えない */
function sameItemIds(a: UnassignedItem[], b: UnassignedItem[] | null): boolean {
  if (b === null || a.length !== b.length) return false;
  return a.every((item, i) => item.id === b[i].id);
}

/**
 * カード配車の未割当リストの楽観削除。
 *
 * カード配車の未割当は `overrideItems` という配列 state を上書きする機構なので、
 * 失敗時は「この操作で消した分」を差分で戻す（全体スナップショットを戻すと、並行する
 * 別の削除まで復活してしまう）。
 *
 * override は入れっぱなしにできない。サーバ再取得で override を消す仕組み
 * (shouldResetOverrideAfterServerRefresh) は override が入っている間は何もしない設計
 * なので、削除でセットした override はここで畳まないと残り続け、以後サーバ側で増えた
 * 未割当案件が画面に出なくなる。そこで**未完了の削除操作を数え、0 件になったら畳む**。
 *
 * Why not「commit の瞬間に畳む」: 削除 API の成功応答が返った時点では refetch は
 * まだ飛んだだけで、`serverItems` には消したはずの案件が残っている。すぐ畳むと
 * refetch が届くまでの間だけ削除済みの案件が画面に出戻る。そのため確定分 (committedIds)
 * がサーバ値から消えるのを待って畳む。実削除が 1 件も起きていない rollback だけの
 * ケースは待つ理由が無いのでその場で畳む（base の `setOverrideUnassigned(null)` と同じ）。
 */
export function useUnassignedRemovalOptimistic(
  params: UseUnassignedRemovalOptimisticParams,
): UnassignedOptimistic {
  const {
    serverItems, overrideItems, setOverrideItems, selectedIds, setSelectedIds, scopeKey,
  } = params;

  // remove / commit / rollback は非同期の応答後に走るので、render 時の値ではなく
  // 「その時点の最新」を読む必要がある。ref の同期は render 中の代入ではなく
  // useLayoutEffect で行う（render を副作用なしに保つ）。
  const latestRef = useRef({ serverItems, overrideItems, setOverrideItems, setSelectedIds, selectedIds });
  useLayoutEffect(() => {
    latestRef.current = { serverItems, overrideItems, setOverrideItems, setSelectedIds, selectedIds };
    // override が誰か（自動配車の完了・scope 変更など）に畳まれたら、こちらの期待値も捨てて
    // 再同期する。古い期待値が残ると以後の削除がいつまでも畳めなくなる
    if (overrideItems === null) settleRef.current.expectedItems = null;
  }, [serverItems, overrideItems, setOverrideItems, setSelectedIds, selectedIds]);

  // rollback / commit を「消した時点の scope」に閉じ込めるための ref。
  // render 中の値だと応答が返るフレームには既に新 scope になっていることがある
  const scopeKeyRef = useRef(scopeKey);
  // 未完了の削除操作の数、成功したがまだサーバ値に反映されていない id、
  // そして「この hook だけで書いたとしたら override はこうなっているはず」の期待値。
  // 同期的に読み書きする必要があるので state ではなく ref で持つ。
  const settleRef = useRef<{
    inFlight: number;
    committedIds: Set<string>;
    expectedItems: UnassignedItem[] | null;
  }>({ inFlight: 0, committedIds: new Set<string>(), expectedItems: null });
  useLayoutEffect(() => {
    scopeKeyRef.current = scopeKey;
    // 旧 scope の操作は下の scope ガードで無視されるので、数え直して持ち越さない
    settleRef.current = { inFlight: 0, committedIds: new Set<string>(), expectedItems: null };
  }, [scopeKey]);

  /**
   * 未完了 0 かつ「確定した削除」がサーバ値から消えていれば override を畳む。
   * 畳めたら true。
   *
   * override は D&D と共用の state なので、削除以外の変更が乗っているときは畳まない。
   * 畳むと未割当リストだけ巻き戻り、車両側 (overrideRowCards) には案件が残ったままに
   * なって二重に見える。判定は「この hook の書き込みだけを積んだ期待値」との突き合わせ。
   * serverItems と直接比べないのは、応答待ちの間にサーバ側で未割当が増えていると
   * 一致しなくなり、増えた案件を出すための畳み込みが二度と起きなくなるため。
   */
  const collapseIfSettled = useCallback((): boolean => {
    const settle = settleRef.current;
    // 他の削除が応答待ちの間に畳むと、その操作で消した案件が復活してしまう
    if (settle.inFlight > 0) return false;
    const { overrideItems: current, serverItems: server, setOverrideItems: setItems } = latestRef.current;
    if (current === null) {
      // 既に畳まれている。待っているものだけ片付ける
      settle.committedIds.clear();
      settle.expectedItems = null;
      return true;
    }
    if (!sameItemIds(current, settle.expectedItems)) return false;
    const serverIds = new Set(server.map((i) => i.id));
    for (const id of settle.committedIds) {
      if (serverIds.has(id)) return false;
    }
    settle.committedIds.clear();
    settle.expectedItems = null;
    setItems(null);
    return true;
  }, []);

  // サーバ値が入れ替わるたびに「確定分が消えたか」を見る。
  // 何も待っていないときに走らせると D&D の override まで毎回消してしまうので、
  // 確定待ちがあるときだけ判定する。
  useEffect(() => {
    if (settleRef.current.committedIds.size === 0) return;
    collapseIfSettled();
  }, [serverItems, collapseIfSettled]);

  // 依存に render ごとの値を入れず、参照を安定させる（呼び出し側が useCallback の
  // 依存に入れるため、変わるたびにハンドラが作り直される）
  return useMemo<UnassignedOptimistic>(() => ({
    remove: (removedItemIds: ReadonlySet<string>) => {
      const scopeAtRemove = scopeKeyRef.current;
      const base = latestRef.current.overrideItems ?? latestRef.current.serverItems;

      const snapshot: RemovalSnapshot = {
        removedItems: base.filter((u) => removedItemIds.has(u.id)),
        removedSelectedIds: Array.from(latestRef.current.selectedIds).filter((id) => removedItemIds.has(id)),
        orderAtRemoval: base.map((u) => u.id),
      };

      settleRef.current.inFlight += 1;
      // 期待値は「実際の override」ではなく自分の書き込み履歴から積む。実際の値から
      // 積むと、既に乗っている D&D の分まで自分の書き込みとして扱ってしまう
      settleRef.current.expectedItems = (settleRef.current.expectedItems ?? latestRef.current.serverItems)
        .filter((u) => !removedItemIds.has(u.id));
      latestRef.current.setOverrideItems(
        (prev) => (prev ?? latestRef.current.serverItems).filter((u) => !removedItemIds.has(u.id)),
      );
      latestRef.current.setSelectedIds((prev) => {
        const next = new Set(prev);
        removedItemIds.forEach((id) => next.delete(id));
        return next;
      });

      // commit / rollback は 1 操作につき 1 回だけ効かせる。二重に数えると
      // inFlight がずれて、応答待ちの他の削除の途中で override を畳んでしまう
      let settled = false;
      const beginSettle = (): boolean => {
        // 旧 scope の応答で現在の日付・会社の表示を触らない
        if (settled || scopeKeyRef.current !== scopeAtRemove) return false;
        settled = true;
        settleRef.current.inFlight = Math.max(0, settleRef.current.inFlight - 1);
        return true;
      };

      return {
        commit: () => {
          if (!beginSettle()) return;
          removedItemIds.forEach((id) => settleRef.current.committedIds.add(id));
          // 大抵はまだサーバ値に残っているので畳めない。上の effect が引き継ぐ
          collapseIfSettled();
        },
        rollback: () => {
          if (!beginSettle()) return;
          latestRef.current.setSelectedIds((prev) => restoreRemovedSelection(prev, snapshot));
          // 畳めるなら override ごと捨てるので、この操作分の復元は要らない。
          // 判定は復元前の形（＝いまの override）で行う
          if (collapseIfSettled()) return;
          settleRef.current.expectedItems = restoreRemovedItems(
            settleRef.current.expectedItems ?? latestRef.current.serverItems,
            snapshot,
          );
          latestRef.current.setOverrideItems(
            (prev) => restoreRemovedItems(prev ?? latestRef.current.serverItems, snapshot),
          );
        },
      };
    },
  }), [collapseIfSettled]);
}
